import {
    CopyToClipboard,
    Input,
    Select,
    SpaceBetween,
    AttributeEditor,
    Button,
    DatePicker,
} from '@amzn/awsui-components-react-v3';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    valuesToOptions,
    valueToOption,
} from '../../../../../../common/utils/cloudscapeHelpers';
import { V_ILT_TYPE } from '../../../../../constants';
import { DAYS_OF_THE_WEEK } from '../../../../../constants/datetime';
import { DeliverySession } from '../../../../../interfaces/activity';
import {
    activityGroupSlice,
    removeDeliverySessionWithValidationClear,
    selectSessionError,
    setSessionValidationError,
} from '../../../../../store/slices/activityGroupSlice';
import { useActivityGroup, useDeliverySessionTypes } from '../../hooks/hooks';
import { AciMessage, AciResouceConstrain } from '../../utils/validations';
import { getOptionsAndLookupForSelectInput } from '../../../../../../imt/components/Instructor/FormSections/FormSections.common';
import { InstructorRoleTypeItemData } from '../../../../../../common/interfaces/businessDataItem/instructorRoleTypeItem';
import { useBusinessDatum } from '../../../../Common/hooks/useBusinessDatum';
import {
    getInstructorRoleTypesList,
    resetInstructorRoleTypesSlice,
    setSelectedInstructorRoleType,
} from '../../../../../../common/store/slices/instructorRoleTypesSlice';
import { containsUuid } from '../../utils/uuidUtils';
import InstructorAttributeEditor from './InstructorAttributeEditor';
import { getDateDisabledReason, isDateEnabled } from './useSessionDatePicker';

interface MeetingLinkValidation {
    isValid: boolean;
    error: string | null;
}

export interface SessionDetailsProps {
    sessions: DeliverySession[];
    deliveryName: string;
    optionNumber: number;
    optionStartTime: string;
    optionStartTimeMeridiem: string;
    optionEndTime: string;
    optionEndTimeMeridiem: string;
    defaultSessionStartTime: number;
    defaultSessionEndTime: number;
    isReadOnlyMode: boolean;
    selectedWeekStartDay: dayjs.Dayjs;
    selectedWeekEndDay: dayjs.Dayjs;
}

const MAX_DELIVERY_SESSION =
    AciResouceConstrain.MAX_DELIVERY_SESSION_PER_ACTIVITY;

const SessionDetails = (props: SessionDetailsProps) => {
    const {
        sessions,
        deliveryName,
        optionNumber,
        optionStartTime,
        optionStartTimeMeridiem,
        optionEndTime,
        optionEndTimeMeridiem,
        defaultSessionStartTime,
        defaultSessionEndTime,
        isReadOnlyMode,
    } = props;

    const [dateInputValues, setDateInputValues] = useState({});

    const dispatch = useDispatch();
    const activityGroup = useActivityGroup();
    const deliverySessionTypes = useDeliverySessionTypes();
    const [optionStartTimeHours, optionStartTimeMinutes] = optionStartTime
        .split(':')
        .map((val) => parseInt(val));
    const [optionEndTimeHours, optionEndTimeMinutes] = optionEndTime
        .split(':')
        .map((val) => parseInt(val));
    const startTimeMeridiemOffsetHours =
        optionStartTimeMeridiem === 'PM' && optionStartTimeHours < 12 ? 12 : 0;
    const endTimeMeridiemOffsetHours =
        optionEndTimeMeridiem === 'PM' && optionEndTimeHours < 12 ? 12 : 0;
    const activityGroupStartDay = dayjs.tz(
        dayjs.unix(activityGroup.start_timestamp),
        activityGroup.selected_timezone,
    );
    const activityGroupEndDay = dayjs.tz(
        dayjs.unix(activityGroup.end_timestamp),
        activityGroup.selected_timezone,
    );

    const addDeliverySession = () =>
        dispatch(
            activityGroupSlice.actions.addDeliverySession({
                deliveryName,
                optionNumber,
                deliverySession: {
                    start_timestamp: defaultSessionStartTime,
                    end_timestamp: defaultSessionEndTime,
                    delivery_session_type: deliverySessionTypes[0],
                    instructors: [],
                    v_ilt_info: {
                        type: V_ILT_TYPE.PVILT,
                    },
                },
            }),
        );

    const setDeliverySession = (deliverySession: DeliverySession) =>
        dispatch(
            activityGroupSlice.actions.setDeliverySession({
                deliveryName,
                optionNumber,
                deliverySession,
            }),
        );

    const removeDeliverySession = (id: string, index: number) =>
        dispatch(
            removeDeliverySessionWithValidationClear({
                deliveryName,
                optionNumber,
                id,
                index,
            }),
        );

    const isNewDeliverySession = (id: string): boolean => {
        return (
            activityGroup?.new_delivery_sessions?.find(
                (deliverySession) => deliverySession.id === id,
            )?.is_new ?? false
        );
    };

    const weekDayOptions = useMemo(() => {
        if (props.selectedWeekStartDay.isBefore(activityGroupStartDay)) {
            return valuesToOptions(
                DAYS_OF_THE_WEEK.slice(
                    activityGroupStartDay.day(),
                    DAYS_OF_THE_WEEK.length + 1,
                ),
            );
        } else if (activityGroupEndDay.isBefore(props.selectedWeekEndDay)) {
            return valuesToOptions(
                DAYS_OF_THE_WEEK.slice(0, activityGroupEndDay.day() + 1),
            );
        }

        return valuesToOptions(DAYS_OF_THE_WEEK);
    }, [
        activityGroupStartDay,
        activityGroupEndDay,
        props.selectedWeekStartDay,
        props.selectedWeekEndDay,
    ]);

    const sessionErrors = useSelector((state) =>
        sessions.map((session) =>
            selectSessionError(state, {
                deliveryName,
                optionNumber,
                sessionId: session.id,
            }),
        ),
    );
    const getMeetingLink = ({
        v_ilt_info: { type, streamyard_url, url },
    }: DeliverySession) =>
        type === V_ILT_TYPE.PVILT ? (streamyard_url ?? '') : (url ?? '');

    const validateUrl = (url: string): MeetingLinkValidation => {
        if (url.length === 0) {
            return {
                isValid: true,
                error: null,
            };
        }

        try {
            new URL(url);
            return {
                isValid: true,
                error: null,
            };
        } catch (error) {
            return {
                isValid: false,
                error: 'Enter a valid URL',
            };
        }
    };

    useEffect(() => {
        dispatch(getInstructorRoleTypesList());
        dispatch(setSelectedInstructorRoleType(null));

        return () => {
            dispatch(resetInstructorRoleTypesSlice());
        };
    }, [dispatch]);

    const {
        instructorRoleTypeList,
        isInstructorRoleTypeListLoading,
        isInstructorRoleTypeListLoaded,
        instructorRoleTypeListError,
    } = useBusinessDatum();

    const { valueOptions: instructorRoleTypeOptions } =
        getOptionsAndLookupForSelectInput<InstructorRoleTypeItemData>(
            instructorRoleTypeList,
            (instructorRoleType: InstructorRoleTypeItemData) => ({
                label: instructorRoleType.instructor_role_type,
                id: instructorRoleType.pk as string,
            }),
        );

    const updateSessionTimestamps = (
        session: DeliverySession,
        dayOfWeek: string,
    ) => {
        const dayOfWeekIndex = DAYS_OF_THE_WEEK.indexOf(dayOfWeek);

        const currentDate = dayjs
            .tz(
                dayjs.unix(session.start_timestamp),
                activityGroup.selected_timezone,
            )
            .day(dayOfWeekIndex);

        const startTimestamp = currentDate
            .hour(optionStartTimeHours + startTimeMeridiemOffsetHours)
            .minute(optionStartTimeMinutes)
            .unix();

        const endTimestamp = currentDate
            .hour(optionEndTimeHours + endTimeMeridiemOffsetHours)
            .minute(optionEndTimeMinutes)
            .unix();

        setDeliverySession({
            ...session,
            start_timestamp: startTimestamp,
            end_timestamp: endTimestamp,
        });

        setDateInputValues((prev) => ({
            ...prev,
            [session.id]: currentDate.format('YYYY-MM-DD'),
        }));
    };

    const definition = [
        {
            label: 'Session Type',
            control: (session) => (
                <Select
                    disabled={isReadOnlyMode}
                    options={valuesToOptions(deliverySessionTypes)}
                    selectedOption={valueToOption(
                        session.delivery_session_type ||
                            deliverySessionTypes[0],
                    )}
                    onChange={({ detail }) => {
                        setDeliverySession({
                            ...session,
                            delivery_session_type: detail.selectedOption.value,
                        });
                    }}
                    data-testid={`session-type-select-${deliveryName}-${optionNumber}`}
                />
            ),
        },
        {
            label: 'Session Date',
            control: (session) => {
                const initialDateValue = dayjs
                    .tz(
                        dayjs.unix(session.start_timestamp),
                        activityGroup.selected_timezone,
                    )
                    .format('YYYY-MM-DD');

                const inputValue =
                    dateInputValues[session.id] ?? initialDateValue;

                return (
                    <DatePicker
                        disabled={isReadOnlyMode}
                        isDateEnabled={(date) =>
                            isDateEnabled(
                                date,
                                activityGroupStartDay,
                                activityGroupEndDay,
                                props.selectedWeekStartDay,
                                props.selectedWeekEndDay,
                            )
                        }
                        dateDisabledReason={(date) =>
                            getDateDisabledReason(
                                date,
                                activityGroupStartDay,
                                activityGroupEndDay,
                                props.selectedWeekStartDay,
                            )
                        }
                        value={inputValue}
                        onChange={({ detail }) => {
                            setDateInputValues((prev) => ({
                                ...prev,
                                [session.id]: detail.value,
                            }));

                            if (!detail.value) {
                                return;
                            }

                            const parsedDate = dayjs(
                                detail.value,
                                'YYYY-MM-DD',
                                true,
                            );

                            if (!parsedDate.isValid()) {
                                return;
                            }

                            const dayOfWeek =
                                DAYS_OF_THE_WEEK[parsedDate.day()];

                            updateSessionTimestamps(session, dayOfWeek);
                        }}
                        placeholder="YYYY/MM/DD"
                        data-testid={`session-date-${deliveryName}-${optionNumber}`}
                    />
                );
            },
        },
        {
            label: 'Session Day',
            control: (session) => {
                return (
                    <Select
                        disabled={isReadOnlyMode}
                        options={weekDayOptions}
                        selectedOption={valueToOption(
                            DAYS_OF_THE_WEEK[
                                dayjs
                                    .tz(
                                        dayjs.unix(session.start_timestamp),
                                        activityGroup.selected_timezone,
                                    )
                                    .day()
                            ],
                        )}
                        onChange={({ detail }) => {
                            updateSessionTimestamps(
                                session,
                                detail.selectedOption.value,
                            );
                        }}
                        data-testid={`session-day-select-${deliveryName}-${optionNumber}`}
                    />
                );
            },
        },

        {
            label: 'Meeting Type',
            control: (session) => (
                <Select
                    selectedOption={valueToOption(session.v_ilt_info.type)}
                    options={valuesToOptions(Object.values(V_ILT_TYPE))}
                    disabled={props.isReadOnlyMode}
                    onChange={({ detail }) => {
                        const newType = detail.selectedOption.value;

                        if (newType === V_ILT_TYPE.WEBEX) {
                            dispatch(
                                activityGroupSlice.actions.clearSessionValidationErrors(
                                    {
                                        deliveryName,
                                        optionNumber,
                                        id: session.id,
                                    },
                                ),
                            );
                        }

                        setDeliverySession({
                            ...session,
                            v_ilt_info: {
                                ...session.v_ilt_info,
                                type: newType,
                                ...(newType === V_ILT_TYPE.WEBEX && {
                                    streamyard_url: null,
                                }),
                            },
                        });
                    }}
                    data-testid={`meeting-type-select-${deliveryName}-${optionNumber}`}
                />
            ),
        },
        {
            label: 'Meeting Link',
            errorText: (session) => {
                const sessionIndex = sessions.indexOf(session);
                return sessionErrors[sessionIndex];
            },
            control: (session) => {
                const meetingLink = getMeetingLink(session);
                const sessionIndex = sessions.indexOf(session);
                const sessionError = sessionErrors[sessionIndex];

                return (
                    <SpaceBetween direction="horizontal" size="s">
                        <Input
                            type="url"
                            value={meetingLink}
                            disabled={
                                props.isReadOnlyMode ||
                                session.v_ilt_info.type !== V_ILT_TYPE.PVILT
                            }
                            onChange={(event) =>
                                setDeliverySession({
                                    ...session,
                                    v_ilt_info: {
                                        ...session.v_ilt_info,
                                        streamyard_url:
                                            event.detail.value
                                                .trimStart()
                                                .trimEnd() || null,
                                    },
                                })
                            }
                            onBlur={() => {
                                const { isValid } = validateUrl(meetingLink);

                                if (!isValid) {
                                    dispatch(
                                        setSessionValidationError({
                                            deliveryName,
                                            optionNumber,
                                            sessionId: session.id,
                                            error: 'Enter a valid URL',
                                        }),
                                    );
                                    return;
                                } else {
                                    dispatch(
                                        activityGroupSlice.actions.clearSessionValidationErrors(
                                            {
                                                deliveryName,
                                                optionNumber,
                                                id: session.id,
                                            },
                                        ),
                                    );
                                }
                            }}
                            data-testid={`streamyard-link-input-${deliveryName}-${optionNumber}-${sessionIndex}`}
                        />
                        {!isReadOnlyMode &&
                            !sessionError &&
                            validateUrl(meetingLink).isValid &&
                            meetingLink.length > 0 && (
                                <CopyToClipboard
                                    copyButtonText="Copy Meeting URL"
                                    copyErrorText="Link failed to copy"
                                    copySuccessText="Meeting Link copied"
                                    textToCopy={meetingLink}
                                    variant="icon"
                                    data-testid={`copy-meeting-link-${deliveryName}-${optionNumber}-${sessionIndex}`}
                                />
                            )}
                    </SpaceBetween>
                );
            },
        },
        {
            control: (session) => (
                <AttributeEditor
                    data-testid="InstructorAttributeEditor"
                    items={session.instructors || []}
                    disableAddButton={isReadOnlyMode}
                    addButtonText="Add instructor"
                    removeButtonText="Remove instructor"
                    isItemRemovable={() => !isReadOnlyMode}
                    onAddButtonClick={() => {
                        const newInstructor = {
                            pk: '',
                            name: '',
                            email: '',
                            type: '',
                            location: '',
                            do_not_shuffle: false,
                            role: '',
                        };
                        dispatch(
                            activityGroupSlice.actions.addInstructorToDeliverySession(
                                {
                                    deliveryName,
                                    optionNumber,
                                    instructor: newInstructor,
                                    deliverySessionId: session.id,
                                },
                            ),
                        );
                    }}
                    onRemoveButtonClick={({ detail }) => {
                        const instructor =
                            session.instructors[detail.itemIndex];
                        dispatch(
                            activityGroupSlice.actions.removeInstructorFromDeliverySession(
                                {
                                    deliveryName,
                                    optionNumber,
                                    instructorPk: instructor.pk,
                                    deliverySessionId: session.id,
                                },
                            ),
                        );
                    }}
                    definition={[
                        {
                            label: 'Instructor(s)',
                            control: (item: any) => {
                                return (
                                    <InstructorAttributeEditor
                                        instructor={item}
                                        deliveryName={deliveryName}
                                        optionNumber={optionNumber}
                                        sessionId={session.id}
                                        isReadOnlyMode={isReadOnlyMode}
                                        instructorPool={
                                            activityGroup.instructor_pool
                                        }
                                    />
                                );
                            },
                        },
                        {
                            label: 'Instructor role',
                            control: (item: any) => {
                                const selectedOption = item.role
                                    ? {
                                          label: item.role,
                                          value: item.role,
                                      }
                                    : null;

                                return (
                                    <Select
                                        statusType={
                                            isInstructorRoleTypeListLoading
                                                ? 'loading'
                                                : instructorRoleTypeListError
                                                  ? 'error'
                                                  : 'finished'
                                        }
                                        onLoadItems={() => {
                                            if (
                                                !isInstructorRoleTypeListLoaded &&
                                                !isInstructorRoleTypeListLoading
                                            ) {
                                                dispatch(
                                                    getInstructorRoleTypesList(),
                                                );
                                            }
                                        }}
                                        errorText="Error fetching instructor roles"
                                        loadingText="Loading instructor roles"
                                        data-testid={`instructor-role-select-${deliveryName}-${optionNumber}`}
                                        disabled={isReadOnlyMode}
                                        placeholder="Select a role"
                                        options={instructorRoleTypeOptions.map(
                                            (option) => ({
                                                label: option.label,
                                                value: option.label,
                                            }),
                                        )}
                                        selectedOption={selectedOption}
                                        onChange={(e) => {
                                            dispatch(
                                                activityGroupSlice.actions.updateInstructorRole(
                                                    {
                                                        deliveryName,
                                                        optionNumber,
                                                        instructorPk: item.pk,
                                                        deliverySessionId:
                                                            session.id,
                                                        role: e.detail
                                                            .selectedOption
                                                            .value,
                                                    },
                                                ),
                                            );
                                        }}
                                    />
                                );
                            },
                        },
                    ]}
                    gridLayout={[
                        {
                            rows: [[6, 3]], // Instructor and Role side by side
                            removeButton: { ownRow: false, width: 'auto' },
                        },
                    ]}
                    empty="No instructors assigned"
                />
            ),
        },
    ];

    return (
        <AttributeEditor
            data-testid="sessions-attribute-editor"
            items={sessions}
            addButtonText="Add Session"
            onAddButtonClick={() => addDeliverySession()}
            empty="No delivery sessions have been added."
            additionalInfo={
                sessions.length >= MAX_DELIVERY_SESSION &&
                AciMessage.MAX_DELIVERY_SESSION_PER_ACTIVITY
            }
            disableAddButton={
                sessions.length >= MAX_DELIVERY_SESSION || isReadOnlyMode
            }
            customRowActions={({ item, itemIndex }) => {
                return (
                    <Button
                        data-testid={`delete-session-button-${deliveryName}-${optionNumber}-${itemIndex}`}
                        disabledReason="Saved delivery sessions cannot be deleted."
                        disabled={
                            isReadOnlyMode ||
                            (containsUuid(item.id) &&
                                !isNewDeliverySession(item.id))
                        }
                        onClick={() => {
                            const session = sessions[itemIndex];
                            removeDeliverySession(session.id, itemIndex);
                        }}
                    >
                        Remove session
                    </Button>
                );
            }}
            definition={definition}
            gridLayout={[
                {
                    rows: [
                        [6, 6], // Session Type, Session Date
                        [6, 6], // Session Day, Meeting Type
                        [12], // Meeting Link
                        [12], // Instructors AttributeEditor
                    ],
                    removeButton: { ownRow: false },
                    breakpoint: 'default',
                },
                {
                    rows: [
                        [2, 3, 2, 2, 3], // All controls in one row,
                        [12], // Instructors AttributeEditor
                    ],
                    removeButton: { ownRow: false, width: 'auto' },
                    breakpoint: 'xs',
                },
                {
                    rows: [
                        [12], // Session Type
                        [12], // Session Date
                        [12], // Session Day
                        [12], // Meeting Type
                        [12], // Meeting Link
                        [12], // Instructors AttributeEditor
                    ],
                    removeButton: { ownRow: false },
                    breakpoint: 'xxs',
                },
            ]}
        />
    );
};

export default SessionDetails;
